import React, { useEffect, useState } from 'react';
import { useFeedback } from '../components/FeedbackProvider';
import { prizeService } from '../services';
import {
  IMAGE_ACCEPT,
  PrizeBasicFields,
  PrizeOptionsEditor,
  PrizeStatusFields,
  buildPrizePayload,
  formFromPrizeItem,
  getEmptyPrizeForm,
  getImageUrl,
  getUploadErrorMessage,
  prepareImageFile,
  validateImageFiles
} from './PrizeAdminForm';

function AdminPrizeEditorModal({ prizeId, draft = null, items = [], onClose, onSaved, onDeleted }) {
  const { confirm } = useFeedback();
  const isNew = !prizeId;
  const [item, setItem] = useState(null);
  const [form, setForm] = useState(getEmptyPrizeForm);
  const [pendingCreateImages, setPendingCreateImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setMessage('');
    setError('');
    setPendingCreateImages([]);

    if (isNew) {
      setItem(null);
      setForm(draft || getEmptyPrizeForm());
      setLoading(false);
      return;
    }

    const nextItem = items.find((entry) => String(entry.id) === String(prizeId));
    if (!nextItem) {
      setItem(null);
      setError('没有找到这个礼物，可能已经被删除。');
      setLoading(false);
      return;
    }

    setItem(nextItem);
    setForm(draft || formFromPrizeItem(nextItem));
    setLoading(false);
  }, [draft, isNew, items, prizeId]);

  const uploadPrizeFiles = async (targetPrizeId, files) => {
    let latestResponse = null;

    for (const file of files) {
      const image = file.data_url ? file : await prepareImageFile(file);
      latestResponse = await prizeService.uploadPrizeImages(targetPrizeId, [image]);
    }

    return latestResponse;
  };

  const refreshItem = (nextItem) => {
    if (!nextItem) return;
    setItem(nextItem);
    setForm(formFromPrizeItem(nextItem));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const payload = buildPrizePayload(form);
      const response = isNew
        ? await prizeService.createAdminItem(payload)
        : await prizeService.updateAdminItem(prizeId, payload);

      let savedItem = response.data;
      if (isNew && pendingCreateImages.length > 0) {
        const uploadResponse = await uploadPrizeFiles(savedItem.id, pendingCreateImages);
        savedItem = uploadResponse?.data || savedItem;
      }

      setPendingCreateImages([]);
      setMessage(isNew ? '礼物已创建' : '礼物已更新');
      refreshItem(savedItem);
      onSaved?.(savedItem, { wasNew: isNew });
    } catch (err) {
      setError(getUploadErrorMessage(err) || '保存礼物失败');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateImageSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      validateImageFiles(files);
      const images = await Promise.all(files.map((file) => prepareImageFile(file)));
      setPendingCreateImages((current) => [...current, ...images]);
    } catch (err) {
      setError(getUploadErrorMessage(err) || '读取图片失败');
    } finally {
      event.target.value = '';
    }
  };

  const removePendingCreateImage = (index) => {
    setPendingCreateImages((current) => current.filter((_, imageIndex) => imageIndex !== index));
  };

  const handleUploadImages = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length || !item) return;

    setUploading(true);
    setError('');
    setMessage('');

    try {
      validateImageFiles(files);
      const response = await uploadPrizeFiles(item.id, files);
      const nextItem = response?.data || item;
      setMessage('图片已上传');
      refreshItem(nextItem);
      onSaved?.(nextItem, { wasNew: false, imagesOnly: true });
    } catch (err) {
      setError(getUploadErrorMessage(err) || '上传图片失败');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const moveImage = async (imageId, direction) => {
    if (!item?.images?.length) return;
    const ids = item.images.map((image) => image.id);
    const index = ids.indexOf(imageId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) return;

    [ids[index], ids[nextIndex]] = [ids[nextIndex], ids[index]];
    try {
      const response = await prizeService.updatePrizeImageOrder(item.id, ids);
      refreshItem(response.data);
      onSaved?.(response.data, { wasNew: false, imagesOnly: true });
    } catch (err) {
      setError(err.response?.data?.message || '调整图片顺序失败');
    }
  };

  const deleteImage = async (imageId) => {
    if (!item) return;
    const confirmed = await confirm({
      title: '删除礼物图片',
      message: '确定删除这张图片吗？',
      confirmText: '删除图片',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      const response = await prizeService.deletePrizeImage(item.id, imageId);
      setMessage('图片已删除');
      refreshItem(response.data);
      onSaved?.(response.data, { wasNew: false, imagesOnly: true });
    } catch (err) {
      setError(err.response?.data?.message || '删除图片失败');
    }
  };

  const handleDeleteItem = async () => {
    if (!item) return;
    const confirmed = await confirm({
      title: '删除礼物',
      message: `确定删除「${item.name}」吗？`,
      detail: '删除后用户兑换页和后台礼物列表都不会再显示它。',
      confirmText: '删除礼物',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await prizeService.deleteAdminItem(item.id);
      onDeleted?.(item);
    } catch (err) {
      setError(err.response?.data?.message || '删除礼物失败');
    }
  };

  return (
    <div className="prize-editor-modal-overlay" role="presentation" onMouseDown={onClose}>
      <div className="prize-editor-modal" role="dialog" aria-modal="true" aria-label={isNew ? '新建礼物' : '编辑礼物'} onMouseDown={(event) => event.stopPropagation()}>
        <header className="prize-editor-modal-header">
          <div>
            <h2>{isNew ? '新建礼物' : '编辑礼物'}</h2>
            <p>管理商品图片、基础信息、价格库存、SKU 子选项和展示状态。</p>
          </div>
          <div className="admin-prize-editor-header-actions">
            {!isNew && item && (
              <button type="button" className="danger-action" onClick={handleDeleteItem}>
                删除礼物
              </button>
            )}
            <button type="button" className="secondary-action" onClick={onClose}>
              关闭
            </button>
          </div>
        </header>

        <div className="prize-editor-modal-body">
          {loading ? (
            <div className="loading">正在加载礼物编辑...</div>
          ) : (
            <>
              {message && <div className="prizes-success">{message}</div>}
              {error && <div className="prizes-error">{error}</div>}

              {isNew ? (
                <section className="prize-editor-panel image-manager full-image-manager top-image-manager">
                  <div className="panel-toolbar">
                    <div>
                      <h2>商品图片</h2>
                      <p>可多次选择图片，保存礼物后按选择顺序追加。</p>
                    </div>
                    <label className="file-button">
                      选择商品图片
                      <input type="file" accept={IMAGE_ACCEPT} multiple onChange={handleCreateImageSelect} />
                    </label>
                  </div>
                  {pendingCreateImages.length > 0 ? (
                    <div className="image-admin-grid pending-image-grid">
                      {pendingCreateImages.map((image, index) => (
                        <div key={`${image.filename || 'pending'}-${index}`} className="image-admin-item">
                          <img src={image.data_url} alt={image.filename || `待上传图片 ${index + 1}`} />
                          <div className="image-admin-actions">
                            <button type="button" className="danger-text" onClick={() => removePendingCreateImage(index)}>移除</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-inline">还没有选择商品图片</div>
                  )}
                </section>
              ) : item && (
                <section className="prize-editor-panel image-manager full-image-manager top-image-manager">
                  <div className="panel-toolbar">
                    <div>
                      <h2>商品图片</h2>
                      <p>上传单张或多张都会追加到现有图片，不会替换原图。</p>
                    </div>
                    <label className="file-button">
                      {uploading ? '上传中...' : '追加商品图片'}
                      <input type="file" accept={IMAGE_ACCEPT} multiple onChange={handleUploadImages} disabled={uploading} />
                    </label>
                  </div>

                  <div className="image-admin-grid">
                    {(item.images || []).map((image, index) => (
                      <div key={image.id} className="image-admin-item">
                        <img src={getImageUrl(image.image_url)} alt={item.name} />
                        <div className="image-admin-actions">
                          <button type="button" onClick={() => moveImage(image.id, -1)} disabled={index === 0}>上移</button>
                          <button type="button" onClick={() => moveImage(image.id, 1)} disabled={index === item.images.length - 1}>下移</button>
                          <button type="button" className="danger-text" onClick={() => deleteImage(image.id)}>删除</button>
                        </div>
                      </div>
                    ))}
                    {(!item.images || item.images.length === 0) && (
                      <div className="empty-inline">还没有图片</div>
                    )}
                  </div>
                </section>
              )}

              <form id="prize-full-editor-modal-form" onSubmit={handleSave} className="prize-full-editor-form">
                <section className="prize-editor-panel">
                  <div className="panel-toolbar">
                    <h2>基础信息</h2>
                  </div>
                  <div className="prize-editor-form">
                    <PrizeBasicFields form={form} setForm={setForm} />
                  </div>
                </section>

                <section className="prize-editor-panel">
                  <div className="panel-toolbar">
                    <h2>子选项</h2>
                  </div>
                  <PrizeOptionsEditor
                    form={form}
                    setForm={setForm}
                    onError={setError}
                    hasParentImages={isNew ? pendingCreateImages.length > 0 : (item?.images || []).length > 0}
                  />
                </section>

                <section className="prize-editor-panel">
                  <div className="panel-toolbar">
                    <h2>展示设置</h2>
                  </div>
                  <div className="prize-editor-form">
                    <PrizeStatusFields form={form} setForm={setForm} />
                  </div>
                </section>
              </form>
            </>
          )}
        </div>

        <footer className="prize-editor-modal-footer">
          <button type="button" className="secondary-action" onClick={onClose}>
            取消
          </button>
          <button className="primary-action" form="prize-full-editor-modal-form" disabled={saving || loading}>
            {saving ? '保存中...' : '保存礼物'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default AdminPrizeEditorModal;
